// @ts-nocheck
import { SizeInput } from "@/components/shared/SizeInput";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useQuery, useMutation } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import {
  productionOrderFormSchema,
  type ProductionOrderFormData,
} from "@/shared/schemas/production-order.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { SaveActionButtons } from "@/components/shared/SaveActionButtons";
import { focusFirstError } from "@/lib/form/formUtils";
import {
  Save,
  Loader2,
  Package,
  Settings,
  Scissors,
  Plus,
  Trash,
  FileText,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Copy,
  Pencil,
  Printer,
  Layers,
  Cpu,
  Wrench,
  RefreshCw,
  Factory,
  ShieldCheck,
  Sparkles,
  Rocket,
} from "lucide-react";
import { zodValidator } from "@tanstack/zod-form-adapter";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { Switch } from "@/components/ui/switch";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RoutingBuilder } from "./components/RoutingBuilder";
import { BOMBuilder } from "./components/BOMBuilder";
import { OrderSourceBuilder } from "./components/OrderSourceBuilder";
import { ComponentTreeEditor } from "./components/ComponentTreeEditor";
import { PlatePlanBuilder } from "./components/PlatePlanBuilder";
import { ToolingBuilder } from "./components/ToolingBuilder";
import { PrintLayoutBuilder } from "./components/PrintLayoutBuilder";
import { MaterialPlanList } from "@/components/planning/MaterialPlanList";
import { POLayout } from "./components/POLayout";
import { POHeader } from "./components/POHeader";
import { mergeUniqueRoutingSteps } from "./components/routing-dedup.helper";
import { POPackagingLogTab } from "./tabs/POPackagingLogTab";
import { useMoldLookup, usePlateLookup, useMaterialLookup } from "@/hooks/useFKLookup";
import {
  buildAutoPlannedMaterialsAndBom,
  detectLaminationRequirement,
  detectMountingRequirements,
  parseSheetDimensionsMm,
} from "./utils/po-auto-planner.engine";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useFormBlocker } from "@/hooks/useFormBlocker";
import { NumericInput } from "@/components/ui/numeric-input";
import { toVnDateString } from "@apv-erp/status";
interface ProductionOrderFormProps {
  orderId?: string;
  /** id nội bộ của lệnh (để lấy nhật ký KCS & Đóng gói khi sửa lệnh) */
  productionOrderDbId?: string;
  defaultValues?: Partial<ProductionOrderFormData>;
  onSubmit: (data: ProductionOrderFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  hideStickyHeader?: boolean;
}

/* ── Helpers ── */
/** Ô kích thước chuẩn (2 ô số R × D, lưu "R x D mm") — thay ô chữ "650x430mm" (04/11/2026) */
function SizeField({ field, label }: { field: any; label: string }) {
  return (
    <TanstackFieldWrapper field={field} label={label}>
      <SizeInput value={field.state.value ?? ""} onChange={(v) => field.handleChange(v)} onBlur={field.handleBlur} testId={`size-${field.name}`} />
    </TanstackFieldWrapper>
  );
}

function TextField({
  field,
  label,
  required,
  placeholder,
  type = "text",
}: {
  field: any;
  label: string;
  required?: boolean;
  placeholder?: string;
  type?: string;
}) {
  return (
    <TanstackFieldWrapper field={field} label={label} required={required}>
      <TanstackFormControl>
        <Input
          id={field.name}
          type={type}
          value={field.state.value ?? ""}
          placeholder={placeholder}
          className="h-9"
          onChange={(e) =>
            field.handleChange(
              type === "number" ? Number(e.target.value) : e.target.value,
            )
          }
          onBlur={field.handleBlur}
        />
      </TanstackFormControl>
    </TanstackFieldWrapper>
  );
}

export default function ProductionOrderForm({
  orderId,
  productionOrderDbId,
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu",
  hideStickyHeader = false,
}: ProductionOrderFormProps) {
  const isEdit = Boolean(orderId);
  const isFirstMountCompleteRef = useRef(false);
  // Dòng đơn hàng đã nạp sẵn (tách lệnh / ghép bài / bài bình): không tự điền lại SL cả dòng đè lên SL người lập lệnh
  const lastFilledOrderDetailIdRef = useRef<string | undefined>((defaultValues as any)?.orderDetailId);

  useEffect(() => {
    const timer = setTimeout(() => {
      isFirstMountCompleteRef.current = true;
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  // Lưu nháp / Lưu / Lưu & Duyệt Ban Hành (plan-draft-and-save-buttons-v1)
  const currentStatus = (defaultValues as any)?.status as string | undefined;
  const isPublishedOrder = Boolean(currentStatus && !["DRAFT", "PENDING_APPROVAL"].includes(currentStatus));
  const saveModeRef = useRef<"DRAFT" | "SUBMIT" | "APPROVE">("SUBMIT");
  const [isDraftSaving, setIsDraftSaving] = useState(false);

  const form = useForm({
    defaultValues: {
      productName: defaultValues?.productName ?? "",
      poReference: defaultValues?.poReference ?? "",
      salesOrderId: defaultValues?.salesOrderId ?? "",
      orderDetailId: defaultValues?.orderDetailId ?? "",
      productId: defaultValues?.productId ?? "",
      customerId: defaultValues?.customerId ?? "",
      quantity: defaultValues?.quantity ?? 0,
      qtyAllowance: defaultValues?.qtyAllowance ?? 0,
      qtyPlan: defaultValues?.qtyPlan ?? 0,
      deadline:
        defaultValues?.deadline ?? toVnDateString(),
      priority: defaultValues?.priority ?? ("NORMAL" as const),
      deploymentModel: defaultValues?.deploymentModel ?? undefined,
      status: defaultValues?.status ?? ("DRAFT" as const),
      notes: defaultValues?.notes ?? "",
      techSpecId: defaultValues?.techSpecId ?? "",
      layoutCode: defaultValues?.layoutCode ?? "",
      toolings: defaultValues?.toolings ?? [],
      prepressPcsPerSheet: defaultValues?.prepressPcsPerSheet ?? null,
      prepressWasteAllowance: defaultValues?.prepressWasteAllowance ?? null,
      prepressWastePercentage: defaultValues?.prepressWastePercentage ?? null,
      totalTargetQty: defaultValues?.totalTargetQty ?? null,
      totalPlanSheets: defaultValues?.totalPlanSheets ?? null,
      purchasedPaperSize: defaultValues?.purchasedPaperSize ?? "",
      printingPaperSize: defaultValues?.printingPaperSize ?? "",
      cutRatio: defaultValues?.cutRatio ?? null,
      totalMaterialRequired: defaultValues?.totalMaterialRequired ?? null,
      specification: defaultValues?.specification ?? null,
      standardQuantity: defaultValues?.standardQuantity ?? null,
      allowance: defaultValues?.allowance ?? null,
      actualQuantity: defaultValues?.actualQuantity ?? null,
      isComposite: Boolean(defaultValues?.isComposite || (defaultValues?.components && defaultValues.components.length > 1)),
      components: defaultValues?.components ?? [],
      orderSources: defaultValues?.orderSources ?? [],
      printLayouts: (defaultValues as any)?.printLayouts ?? [],
      materialPlans: (defaultValues as any)?.materialPlans ?? [],
      dieCutIds: defaultValues?.dieCutIds ?? [],
      plateIds: defaultValues?.plateIds ?? [],
    },
    // @ts-ignore
    validatorAdapter: zodValidator(),
    validators: { onChange: productionOrderFormSchema as any },
    // "Lưu & Duyệt Ban Hành" không gửi saveMode: server giữ RELEASED và tự kiểm tra quyền duyệt
    onSubmit: async ({ value }: any) =>
      onSubmit({
        ...(value as ProductionOrderFormData),
        saveMode: saveModeRef.current === "APPROVE" ? undefined : saveModeRef.current,
      } as any),
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    saveModeRef.current = "SUBMIT";
    form.handleSubmit();
    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  const isDirty = useStore(form.store, (state: any) => state.isDirty);
  const isSubmittingForm = useStore(
    form.store,
    (state: any) => state.isSubmitting,
  );
  useFormBlocker(isDirty && !isSubmittingForm && !isSubmitting);

  const { hasPermission } = useRoleAccess();

  const [customerModal, setCustomerModal] = useState<{
    open: boolean;
    name: string;
  }>({ open: false, name: "" });
  const [productModal, setProductModal] = useState<{
    open: boolean;
    name: string;
  }>({ open: false, name: "" });
  const [moldModal, setMoldModal] = useState<{ open: boolean; name: string }>({
    open: false,
    name: "",
  });
  const [allowancePct, setAllowancePct] = useState<string>("1");
  const [inventory, setInventory] = useState<string>("0");

  // Tooling lookups — auto-filter by selected product/customer
  const selectedProductId = useStore(
    form.store,
    (s: any) => s.values.productId,
  );
  const selectedCustomerId = useStore(
    form.store,
    (s: any) => s.values.customerId,
  );
  const selectedSalesOrderId = useStore(
    form.store,
    (s: any) => s.values.salesOrderId,
  );
  const selectedOrderDetailId = useStore(
    form.store,
    (s: any) => s.values.orderDetailId,
  );

  const { data, isLoading: isLoadingOrder } = useQuery({
    ...trpc.orders.getById.queryOptions({ id: selectedSalesOrderId as string }),
    enabled: !!selectedSalesOrderId,
  });
  const orderData = data as any;

  // Auto-cascade customerId from orderData if not set
  useEffect(() => {
    if (!orderData?.customerId) return;
    const currentCustId = (form.state.values as any).customerId;
    if (!currentCustId || currentCustId !== orderData.customerId) {
      (form as any).setFieldValue("customerId", orderData.customerId);
    }
  }, [orderData, form]);

  useEffect(() => {
    if (isEdit || !isFirstMountCompleteRef.current) return;
    if (orderData?.orderDetails && orderData.orderDetails.length === 1) {
      const detail = orderData.orderDetails[0];
      if (detail.id !== selectedOrderDetailId) {
        (form as any).setFieldValue("orderDetailId", detail.id);
      }
    }
  }, [orderData, form, selectedOrderDetailId, isEdit]);

  useEffect(() => {
    if (isEdit || !isFirstMountCompleteRef.current) return;
    if (!selectedOrderDetailId || !Array.isArray(orderData?.orderDetails)) return;
    const detail = (orderData.orderDetails || []).find(
      (d: any) => d.id === selectedOrderDetailId,
    );
    if (!detail) return;
    // Chỉ tự điền khi người dùng đổi sang dòng khác (không chạy lại cho dòng nạp sẵn khi đơn hàng tải xong)
    if (selectedOrderDetailId === lastFilledOrderDetailIdRef.current) return;
    lastFilledOrderDetailIdRef.current = selectedOrderDetailId;

    if (detail.productId && detail.productId !== selectedProductId) {
      (form as any).setFieldValue("productId", detail.productId);
    }
    if (
      detail.productName &&
      detail.productName !== (form.state.values as any).productName
    ) {
      (form as any).setFieldValue("productName", detail.productName);
    }
    if (
      detail.quantity &&
      detail.quantity !== (form.state.values as any).quantity
    ) {
      (form as any).setFieldValue("quantity", detail.quantity);
    }
    if (
      detail.poReference &&
      detail.poReference !== (form.state.values as any).poReference
    ) {
      (form as any).setFieldValue("poReference", detail.poReference);
    }
  }, [selectedOrderDetailId, orderData, form, selectedProductId, isEdit]);

  const totalMaterialRequired = useStore(
    form.store,
    (s: any) => s.values.totalMaterialRequired,
  );
  const orderSources = useStore(
    form.store,
    (s: any) => s.values.orderSources,
  ) || [];

  const { data: fullProduct } = useQuery({
    ...trpc.products.getById.queryOptions(
      { id: selectedProductId as string },
      { enabled: !!selectedProductId },
    ),
    staleTime: 60000,
  });

  const productInfo = useMemo(() => {
    const selectedProduct = fullProduct;
    if (!selectedProduct) return null;

    let specsObj: any = {};
    try {
      if (typeof selectedProduct.specs === "object" && selectedProduct.specs)
        specsObj = selectedProduct.specs;
      else if (
        typeof selectedProduct.specs === "string" &&
        selectedProduct.specs.startsWith("{")
      )
        specsObj = JSON.parse(selectedProduct.specs);
    } catch {}

    const materials =
      selectedProduct.productMaterials
        ?.map((m: any) => m.material?.name || m.materialId)
        .join(", ") ||
      selectedProduct.material?.name ||
      "";
    const processArr =
      selectedProduct.productProcesses?.map(
        (p: any) => p.process?.name || p.processId,
      ) || (selectedProduct.process ? selectedProduct.process.split(",") : []);

    return {
      image: selectedProduct.image || "",
      code: selectedProduct.productCode || "",
      material: materials,
      materialId:
        selectedProduct.productMaterials?.[0]?.materialId ||
        selectedProduct.materialId ||
        "",
      process: processArr.join(", "),
      paperSize: specsObj.size || specsObj.legacy || "",
      printSize: specsObj.printSize || "",
      outs: specsObj.outs || "",
    };
  }, [selectedProductId, fullProduct]);

  // Auto-fill components from product processes
  useEffect(() => {
    if (isEdit || !isFirstMountCompleteRef.current) return;
    if (!selectedProductId || !fullProduct) return;
    if (
      !fullProduct.productProcesses ||
      fullProduct.productProcesses.length === 0
    )
      return;

    const currentComponents = (form.state.values as any).components;
    // Only auto-fill if components array is empty
    if (!currentComponents || currentComponents.length === 0) {
      const routingSteps = mergeUniqueRoutingSteps(
        fullProduct.productProcesses,
        [],
      );

      const bomItems =
        fullProduct.productMaterials?.map((m: any) => ({
          materialId: m.materialId || "",
          qtyRequired: m.quantity || m.standardQuantity || 0,
          wastagePercent: m.wastePercent || 0,
          unit: m.unit || m.material?.unit || "",
          notes: m.note || m.notes || "",
        })) || [];

      (form as any).setFieldValue("components", [
        {
          isMain: true,
          componentName: "Main",
          routingSteps,
          bomItems,
        },
      ]);
    }
  }, [selectedProductId, fullProduct, form]);

  // Sync totalMaterialRequired to main component BOM paper material
  useEffect(() => {
    if (
      !selectedProductId ||
      totalMaterialRequired === undefined ||
      totalMaterialRequired === null
    )
      return;
    const mainPaperMaterialId = productInfo?.materialId;
    if (!mainPaperMaterialId) return;

    const currentComponents = (form.state.values as any).components;
    if (!currentComponents || currentComponents.length === 0) return;

    const newComponents = [...currentComponents];
    const mainComp = { ...newComponents[0] };

    if (mainComp.bomItems && Array.isArray(mainComp.bomItems)) {
      const paperIndex = mainComp.bomItems.findIndex(
        (b: any) => b.materialId === mainPaperMaterialId,
      );
      if (paperIndex !== -1) {
        if (
          mainComp.bomItems[paperIndex].qtyRequired !== totalMaterialRequired
        ) {
          const newBomItems = [...mainComp.bomItems];
          newBomItems[paperIndex] = {
            ...newBomItems[paperIndex],
            qtyRequired: totalMaterialRequired,
          };
          mainComp.bomItems = newBomItems;
          newComponents[0] = mainComp;
          (form as any).setFieldValue("components", newComponents);
        }
      }
    }
  }, [totalMaterialRequired, selectedProductId, productInfo, form]);
 
  // Reactive Cascade on orderSources (Multi-Product / Gang-Run PO)
  useEffect(() => {
    if (!orderSources || orderSources.length <= 1) return;

    // 1. Calculate aggregated Gang-Run numbers
    const totalAllocQty = orderSources.reduce(
      (sum: number, it: any) => sum + (Number(it.plannedQty ?? it.quantity) || 0),
      0
    );

    let maxBaseSheets = 0;
    let sumUps = 0;
    for (const it of orderSources) {
      const q = Number(it.plannedQty ?? it.quantity) || 0;
      const ups = Number(it.pcsPerSheet ?? it.ups) || 1;
      sumUps += ups;
      const sheets = Math.ceil(q / ups);
      if (sheets > maxBaseSheets) {
        maxBaseSheets = sheets;
      }
    }

    const existingWaste = Number((form.state.values as any).prepressWasteAllowance);
    const wasteAllowance = existingWaste > 0
      ? existingWaste
      : (maxBaseSheets > 0 ? Math.max(50, Math.ceil(maxBaseSheets * 0.05)) : 0);
    const runSheetsNeeded = maxBaseSheets > 0 ? maxBaseSheets + wasteAllowance : 0;

    // Update quantities if different
    const currentQtyOrder = (form.state.values as any).qtyOrder;
    const currentUps = (form.state.values as any).ups;
    const currentSheets = (form.state.values as any).totalMaterialRequired;

    if (totalAllocQty > 0 && currentQtyOrder !== totalAllocQty) {
      (form as any).setFieldValue("qtyOrder", totalAllocQty);
      (form as any).setFieldValue("qtyAllowance", wasteAllowance);
      (form as any).setFieldValue("qtyPlan", totalAllocQty + Math.ceil(totalAllocQty * 0.05));
    }
    if (sumUps > 0 && currentUps !== sumUps) {
      (form as any).setFieldValue("ups", sumUps);
      (form as any).setFieldValue("prepressPcsPerSheet", sumUps);
    }
    if (runSheetsNeeded > 0 && currentSheets !== runSheetsNeeded) {
      (form as any).setFieldValue("totalMaterialRequired", runSheetsNeeded);
      (form as any).setFieldValue("runSheetsNeeded", runSheetsNeeded);
      (form as any).setFieldValue("totalPlanSheets", runSheetsNeeded);
    }

    // 2. Client-side Homogeneity check warning (Invariant 3)
    const materials = orderSources
      .map((it: any) => it.materialId || it.detail?.materialId || it.detail?.product?.materialId)
      .filter(Boolean);
    const uniqueMats = Array.from(new Set(materials));
    if (uniqueMats.length > 1) {
      toast.warning("Lưu ý: Các sản phẩm ghép bài đang có chất liệu giấy khác nhau!");
    }

    // 3. Merge Routing Steps across all orderSources
    const validSourcesWithProcs = orderSources.filter(
      (it: any) => (it.productProcesses && it.productProcesses.length > 0) || it.orderDetailId
    );
    if (validSourcesWithProcs.length > 1) {
      const merged = mergeUniqueRoutingSteps(
        fullProduct?.productProcesses || validSourcesWithProcs[0]?.productProcesses || [],
        validSourcesWithProcs.slice(1).map((it: any) => ({
          processes: it.productProcesses || [],
          productCode: it.productCode || it.productName,
          productName: it.productName,
          quantity: Number(it.plannedQty ?? it.quantity) || 0,
          orderDetailId: it.orderDetailId,
        })),
        {
          runSheetsNeeded,
          primaryProductName: fullProduct?.productName || validSourcesWithProcs[0]?.productName,
          primaryProductCode: fullProduct?.productCode || validSourcesWithProcs[0]?.productCode,
          primaryQuantity: Number(validSourcesWithProcs[0]?.plannedQty ?? validSourcesWithProcs[0]?.quantity) || 0,
          isMultiProduct: true,
        }
      );

      const currentComponents = (form.state.values as any).components;
      const currentSteps = currentComponents?.[0]?.routingSteps || [];
      const isRoutingUserCustomized = Boolean(currentComponents?.[0]?.isRoutingUserCustomized);
      const isStepsDifferent =
        currentSteps.length !== merged.length ||
        merged.some((m, idx) => m.name !== currentSteps[idx]?.name || m.stepId !== currentSteps[idx]?.stepId);

      if (!isRoutingUserCustomized && isStepsDifferent && merged.length > 0) {
        const newComponents = [...(currentComponents || [{}])];
        newComponents[0] = {
          ...(newComponents[0] || {}),
          isMain: true,
          componentName: "Main",
          routingSteps: merged,
        };
        (form as any).setFieldValue("components", newComponents);
      }
    }
  }, [orderSources, form, fullProduct]);

  // ── Auto-Planner Engine for All 5 Sub-Tables (Paper, Lamination Film 645/425mm, Flute/Paper/Chipboard Mounting, Toolings, Routing, Finishing) ──
  const { options: materialLookupOptions } = useMaterialLookup(true, "", 500);
  const catalogMaterials = useMemo(
    () => (materialLookupOptions || []).map((o: any) => o.original).filter(Boolean),
    [materialLookupOptions]
  );

  const printingPaperSizeVal = useStore(form.store, (s: any) => s.values.printingPaperSize);
  const purchasedPaperSizeVal = useStore(form.store, (s: any) => s.values.purchasedPaperSize);
  const specificationVal = useStore(form.store, (s: any) => s.values.specification);
  const standardQuantityVal = useStore(form.store, (s: any) => s.values.standardQuantity);
  const allowanceVal = useStore(form.store, (s: any) => s.values.allowance);
  const totalPlanSheetsVal = useStore(form.store, (s: any) => s.values.totalPlanSheets);
  const prepressWasteAllowanceVal = useStore(form.store, (s: any) => s.values.prepressWasteAllowance);
  const notesVal = useStore(form.store, (s: any) => s.values.notes);
  const mainRoutingSteps = useStore(
    form.store,
    (s: any) => s.values.components?.[0]?.routingSteps
  ) || [];

  const runAutoPlanAllSubTables = React.useCallback(
    (forceResetCustom = false) => {
      const vals = (form.state.values as any) || {};
      const status = vals.status || "DRAFT";
      if (["RELEASED", "IN_PROGRESS", "MANUFACTURED", "COMPLETED", "CLOSED"].includes(status)) {
        return;
      }

      const rawSheetSize =
        vals.printingPaperSize ||
        vals.purchasedPaperSize ||
        productInfo?.printSize ||
        productInfo?.paperSize ||
        "650x430";

      const dims = parseSheetDimensionsMm(rawSheetSize);
      if (!dims) return;

      // Compute required sheets & wastage sheets
      const sources = Array.isArray(vals.orderSources) ? vals.orderSources : [];
      let maxBaseSheets = 0;
      for (const it of sources) {
        const q = Number(it.plannedQty ?? it.quantity) || 0;
        const ups = Number(it.pcsPerSheet ?? it.ups) || 1;
        const s = Math.ceil(q / ups);
        if (s > maxBaseSheets) maxBaseSheets = s;
      }

      const totalPlan = Number(vals.totalPlanSheets || vals.totalMaterialRequired || 0);
      const wasteSheets = Math.max(
        0,
        Number(
          vals.prepressWasteAllowance ??
            vals.allowance ??
            (maxBaseSheets > 0 ? Math.max(50, Math.ceil(maxBaseSheets * 0.05)) : 100)
        )
      );
      const reqSheets = Math.max(
        0,
        Number(
          vals.standardQuantity ||
            (totalPlan > wasteSheets
              ? totalPlan - wasteSheets
              : maxBaseSheets || totalPlan || Number(vals.quantity || 1000))
        )
      );

      // Collect process tokens from product, routingSteps, orderSources, specification
      const stepsList = vals.components?.[0]?.routingSteps || [];
      const processTokens: string[] = [];
      if (productInfo?.process) processTokens.push(productInfo.process);
      for (const st of stepsList) {
        if (st.stepId) processTokens.push(String(st.stepId));
        if (st.name) processTokens.push(String(st.name));
        if (st.processName) processTokens.push(String(st.processName));
        if (st.technicalRequirements) processTokens.push(String(st.technicalRequirements));
      }

      const paperSpec =
        vals.specification ||
        sources[0]?.paperType ||
        productInfo?.material ||
        "Ivory 300";

      const existingMaterialPlans = forceResetCustom ? [] : vals.materialPlans || [];
      const existingBomItems = forceResetCustom
        ? []
        : vals.components?.[0]?.bomItems || [];

      const planned = buildAutoPlannedMaterialsAndBom({
        sheetSize: rawSheetSize,
        paperSpec,
        requiredSheets: reqSheets,
        wastageSheets: wasteSheets,
        processes: processTokens,
        specification: `${vals.specification || ""} ${productInfo?.process || ""}`,
        orderSources: sources,
        notes: vals.notes,
        catalogMaterials,
        existingMaterialPlans,
        existingBomItems,
      });

      // 1. Update MaterialPlanList (Tab 2)
      (form as any).setFieldValue("materialPlans", planned.materialPlans);

      // 2. Update BOMBuilder & RoutingSteps on Main Component (Tab 2 & Tab 4)
      const currentComponents = [...(vals.components || [{ isMain: true, componentName: "Main", routingSteps: [], bomItems: [] }])];
      const mainComp = { ...(currentComponents[0] || { isMain: true, componentName: "Main" }) };
      mainComp.bomItems = planned.bomItems;

      // Ensure RoutingSteps includes Cán màng / Bồi sóng if detected and not locked by user
      const isRoutingCustom = !forceResetCustom && Boolean(mainComp.isRoutingUserCustomized);
      if (!isRoutingCustom) {
        const lamReq = detectLaminationRequirement({
          processes: processTokens,
          specification: vals.specification,
          orderSources: sources,
          notes: vals.notes,
        });
        const mountingReqs = detectMountingRequirements({
          processes: processTokens,
          specification: vals.specification,
          orderSources: sources,
          notes: vals.notes,
        });

        let nextSteps = Array.isArray(mainComp.routingSteps) ? [...mainComp.routingSteps] : [];
        if (nextSteps.length === 0) {
          const totalSheetRun = reqSheets + wasteSheets;
          const totalGoodPcs = Number(vals.totalTargetQty || vals.qtyOrder || vals.quantity || 1000);
          nextSteps = [
            {
              id: "step-cat-giay",
              stepOrder: 1,
              stepId: "CAT_GIAY",
              name: "Xả giấy / Cắt giấy in",
              inputQty: totalSheetRun,
              plannedQty: totalSheetRun,
              expectedOutput: totalSheetRun,
              standardQuantity: totalSheetRun,
              productionUnit: "SHEET",
              unit: "Tờ",
              plannedSetupTime: 15,
              plannedSpeed: 5000,
              plannedSetupWaste: 10,
              plannedRunWastePct: 0.5,
              estDurationHours: Number((15 / 60 + totalSheetRun / 5000).toFixed(1)),
              plannedProductionTime: 30,
            },
            {
              id: "step-in-offset",
              stepOrder: 2,
              stepId: "IN_OFFSET",
              name: `In Offset (${dims.formattedMm}mm)`,
              inputQty: totalSheetRun,
              plannedQty: reqSheets,
              expectedOutput: reqSheets,
              standardQuantity: reqSheets,
              productionUnit: "SHEET",
              unit: "Tờ",
              plannedSetupTime: 45,
              plannedSpeed: 7000,
              plannedSetupWaste: 150,
              plannedRunWastePct: 2.0,
              estDurationHours: Number((45 / 60 + reqSheets / 7000).toFixed(1)),
              plannedProductionTime: 90,
            },
          ];
          if (lamReq.required) {
            nextSteps.push({
              id: "step-can-mang",
              stepOrder: nextSteps.length + 1,
              stepId: "CAN_MANG",
              name:
                lamReq.filmType === "MANG_BONG"
                  ? "Cán màng BOPP Bóng"
                  : lamReq.filmType === "MANG_METALIZE"
                    ? "Cán màng Metalize"
                    : "Cán màng BOPP Mờ",
              inputQty: reqSheets,
              plannedQty: reqSheets,
              expectedOutput: reqSheets,
              standardQuantity: reqSheets,
              productionUnit: "SHEET",
              unit: "Tờ",
              plannedSetupTime: 20,
              plannedSpeed: 2100,
              plannedSetupWaste: 20,
              plannedRunWastePct: 1.0,
              estDurationHours: Number((20 / 60 + reqSheets / 2100).toFixed(1)),
              plannedProductionTime: 60,
            });
          }
          if (mountingReqs.length > 0) {
            for (const m of mountingReqs) {
              nextSteps.push({
                id: `step-boi-${m.kind.toLowerCase()}`,
                stepOrder: nextSteps.length + 1,
                stepId: "BOI_SONG",
                name:
                  m.kind === "BOI_SONG"
                    ? `Bồi sóng ${m.fluteType || "E"}`
                    : m.kind === "BOI_CHIPBOARD"
                      ? `Bồi Chipboard ${m.gsmValue || "1200"}gsm`
                      : `Bồi giấy ${m.paperCode || "D250"}`,
                inputQty: reqSheets,
                plannedQty: reqSheets,
                expectedOutput: reqSheets,
                standardQuantity: reqSheets,
                productionUnit: "SHEET",
                unit: "Tờ",
                plannedSetupTime: 30,
                plannedSpeed: 3000,
                plannedSetupWaste: 25,
                plannedRunWastePct: 1.5,
                estDurationHours: Number((30 / 60 + reqSheets / 3000).toFixed(1)),
                plannedProductionTime: 60,
              });
            }
          }
          nextSteps.push(
            {
              id: "step-be",
              stepOrder: nextSteps.length + 1,
              stepId: "BE",
              name: "Bế hộp / Bế định hình",
              inputQty: reqSheets,
              plannedQty: reqSheets,
              expectedOutput: reqSheets,
              standardQuantity: reqSheets,
              productionUnit: "SHEET",
              unit: "Tờ",
              plannedSetupTime: 30,
              plannedSpeed: 3000,
              plannedSetupWaste: 20,
              plannedRunWastePct: 1.0,
              estDurationHours: Number((30 / 60 + reqSheets / 3000).toFixed(1)),
              plannedProductionTime: 60,
            },
            {
              id: "step-dan",
              stepOrder: nextSteps.length + 2,
              stepId: "DAN",
              name: "Dán hộp thành phẩm",
              inputQty: totalGoodPcs,
              plannedQty: totalGoodPcs,
              expectedOutput: totalGoodPcs,
              standardQuantity: totalGoodPcs,
              productionUnit: "PCS",
              unit: "Cái",
              plannedSetupTime: 20,
              plannedSpeed: 8000,
              plannedSetupWaste: 20,
              plannedRunWastePct: 1.0,
              estDurationHours: Number((20 / 60 + totalGoodPcs / 8000).toFixed(1)),
              plannedProductionTime: 60,
            },
            {
              id: "step-kcs",
              stepOrder: nextSteps.length + 3,
              stepId: "KCS",
              name: "KCS & Đóng gói hoàn thiện",
              inputQty: totalGoodPcs,
              plannedQty: totalGoodPcs,
              expectedOutput: totalGoodPcs,
              standardQuantity: totalGoodPcs,
              productionUnit: "PCS",
              unit: "Cái",
              plannedSetupTime: 15,
              plannedSpeed: 4000,
              plannedSetupWaste: 0,
              plannedRunWastePct: 0.5,
              estDurationHours: Number((15 / 60 + totalGoodPcs / 4000).toFixed(1)),
              plannedProductionTime: 45,
            }
          );
        } else {
          // Ensure CAN_MANG / BOI_SONG are injected if newly added in specification
          const hasLamStep = nextSteps.some((s: any) =>
            String(`${s.stepId || ""} ${s.name || ""}`).toLowerCase().match(/can_mang|cán màng|lamination/)
          );
          if (lamReq.required && !hasLamStep) {
            nextSteps.splice(Math.min(2, nextSteps.length), 0, {
              id: `step-can-mang-${Date.now()}`,
              stepOrder: 3,
              stepId: "CAN_MANG",
              name: lamReq.filmType === "MANG_BONG" ? "Cán màng BOPP Bóng" : "Cán màng BOPP Mờ",
              inputQty: reqSheets,
              plannedQty: reqSheets,
              expectedOutput: reqSheets,
              standardQuantity: reqSheets,
              productionUnit: "SHEET",
              unit: "Tờ",
              plannedSetupTime: 20,
              plannedSpeed: 2100,
              plannedSetupWaste: 20,
              plannedRunWastePct: 1.0,
              estDurationHours: Number((20 / 60 + reqSheets / 2100).toFixed(1)),
              plannedProductionTime: 60,
            });
          }
          const hasBoiStep = nextSteps.some((s: any) =>
            String(`${s.stepId || ""} ${s.name || ""}`).toLowerCase().match(/boi|bồi|mounting/)
          );
          if (mountingReqs.length > 0 && !hasBoiStep) {
            const m = mountingReqs[0];
            nextSteps.splice(Math.min(3, nextSteps.length), 0, {
              id: `step-boi-${Date.now()}`,
              stepOrder: 4,
              stepId: "BOI_SONG",
              name:
                m.kind === "BOI_SONG"
                  ? `Bồi sóng ${m.fluteType || "E"}`
                  : m.kind === "BOI_CHIPBOARD"
                    ? `Bồi Chipboard ${m.gsmValue || "1200"}gsm`
                    : `Bồi giấy ${m.paperCode || "D250"}`,
              inputQty: reqSheets,
              plannedQty: reqSheets,
              expectedOutput: reqSheets,
              standardQuantity: reqSheets,
              productionUnit: "SHEET",
              unit: "Tờ",
              plannedSetupTime: 30,
              plannedSpeed: 3000,
              plannedSetupWaste: 25,
              plannedRunWastePct: 1.5,
              estDurationHours: Number((30 / 60 + reqSheets / 3000).toFixed(1)),
              plannedProductionTime: 60,
            });
          }
          nextSteps = nextSteps.map((s: any, idx: number) => ({ ...s, stepOrder: idx + 1 }));
        }
        mainComp.routingSteps = nextSteps;
      }

      currentComponents[0] = mainComp;
      (form as any).setFieldValue("components", currentComponents);

      // 3. Auto-fill Toolings (CTP Plates + Die-Cut Molds) on Tab 3 if empty or forceResetCustom
      const currentToolings = Array.isArray(vals.toolings) ? vals.toolings : [];
      const existingPlates = currentToolings.filter(
        (t: any) => t.toolingType === "PLATE" || t.toolingType === "ZINC"
      );
      const existingMolds = currentToolings.filter(
        (t: any) => t.toolingType !== "PLATE" && t.toolingType !== "ZINC"
      );

      // Kẽm: không tự sinh (số màu, khổ kẽm và máy in do người lập kế hoạch khai báo ở tab 3)
      const nextPlates = existingPlates;

      let nextMolds = existingMolds;
      if (existingMolds.length === 0 || forceResetCustom) {
        if (sources.length > 0) {
          nextMolds = sources.map((src: any, idx: number) => {
            const code = src.dieCutId || src.productCode || `KB-${idx + 1}`;
            return {
              id: `mold-auto-${idx + 1}`,
              toolingId: code.startsWith("KB") || code.startsWith("KHUON") ? code : `KHUON-BE-${code}`,
              toolingName: `Khuôn bế ${src.productName || code}`,
              toolingType: "DIE_CUT",
              status: src.dieCutId ? "REUSE" : "NEW",
              location: "",
              dimensions: "",
              notes: `Khuôn bế cho ${src.productName || code} (${src.pcsPerSheet || 1} con/tờ)`,
            };
          });
        } else if (vals.productName || productInfo?.code) {
          const code = productInfo?.code || "SP01";
          nextMolds = [
            {
              id: "mold-auto-1",
              toolingId: `KHUON-BE-${code}`,
              toolingName: `Khuôn bế ${vals.productName || code}`,
              toolingType: "DIE_CUT",
              status: "NEW",
              location: "",
              dimensions: "",
              notes: "",
            },
          ];
        }
      }

      if (nextPlates !== existingPlates || nextMolds !== existingMolds) {
        (form as any).setFieldValue("toolings", [...nextPlates, ...nextMolds]);
      }

      // Màng / vật tư bồi đã nằm trong dòng Kế hoạch vật tư (không còn khối thông số gia công riêng)

      if (forceResetCustom) {
        toast.success("Đã tính lại tự động toàn bộ Kế hoạch Vật tư, Khuôn kẽm & Công đoạn theo Khổ in!");
      }
    },
    [form, productInfo, catalogMaterials]
  );

  // Trigger auto-planning reactively when key inputs change
  const lastAutoPlanSigRef = useRef<string>("");
  const initialEditUserTriggerSigRef = useRef<string | null>(null);
  useEffect(() => {
    const routingSummary = (mainRoutingSteps || [])
      .map((s: any) => `${s.stepId || ""}:${s.name || ""}`)
      .join(",");
    const sourcesSummary = (orderSources || [])
      .map(
        (s: any) =>
          `${s.orderDetailId || s.productName || ""}:${s.plannedQty ?? s.quantity ?? 0}:${s.pcsPerSheet ?? 1}:${JSON.stringify(s.processes || "")}`
      )
      .join(";");

    const userTriggerSig = [
      printingPaperSizeVal || "",
      purchasedPaperSizeVal || "",
      specificationVal || "",
      standardQuantityVal || "",
      allowanceVal || "",
      totalPlanSheetsVal || "",
      prepressWasteAllowanceVal || "",
      totalMaterialRequired || "",
      notesVal || "",
      routingSummary,
      sourcesSummary,
    ].join("||");

    const sig = [
      userTriggerSig,
      productInfo?.code || "",
      productInfo?.process || "",
      catalogMaterials.length,
    ].join("||");

    if (sig === lastAutoPlanSigRef.current) return;
    lastAutoPlanSigRef.current = sig;

    const currentPlans = (form.state.values as any)?.materialPlans || [];
    if (isEdit && currentPlans.length > 0) {
      if (initialEditUserTriggerSigRef.current === null) {
        initialEditUserTriggerSigRef.current = userTriggerSig;
        return;
      }
      if (userTriggerSig === initialEditUserTriggerSigRef.current) {
        return;
      }
    }

    runAutoPlanAllSubTables(false);
  }, [
    printingPaperSizeVal,
    purchasedPaperSizeVal,
    specificationVal,
    standardQuantityVal,
    allowanceVal,
    totalPlanSheetsVal,
    prepressWasteAllowanceVal,
    totalMaterialRequired,
    notesVal,
    productInfo,
    mainRoutingSteps,
    orderSources,
    catalogMaterials.length,
    isEdit,
    form,
    runAutoPlanAllSubTables,
  ]);

  const checkStockMutation = useMutation(trpc.inventory.checkStockForBOM.mutationOptions());
  const [isApproving, setIsApproving] = React.useState(false);
  const canApproveRelease = hasPermission("production-orders", "approve");

  // Lưu nháp: ghi tạm thông tin đang có, chỉ người tạo / Admin / Giám đốc thấy
  const handleSaveDraft = async () => {
    saveModeRef.current = "DRAFT";
    setIsDraftSaving(true);
    try {
      await onSubmit({ ...(form.state.values as any), saveMode: "DRAFT" } as any);
    } finally {
      setIsDraftSaving(false);
      saveModeRef.current = "SUBMIT";
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      // TanStack Form không có getValues (API của react-hook-form) → nút "Lưu & Duyệt Ban Hành" luôn lỗi TypeError
      const components = (form.getFieldValue("components") as any[]) || [];
      const allBom = components.flatMap(c => c.bomItems || []);
      const itemsToCheck = allBom.filter(b => b.materialId).map(b => ({
        itemId: b.materialId,
        itemType: "MATERIAL",
        requiredQty: b.plannedQuantity || 1, // Fallback
      }));

      if (itemsToCheck.length > 0) {
        const shortages = await checkStockMutation.mutateAsync({ items: itemsToCheck });
        if (shortages && shortages.length > 0) {
          toast.warning(
            `Cảnh báo tồn kho: Thiếu ${shortages.length} mã vật tư — Yêu cầu tạo phiếu mua hàng.`,
            { duration: 5000 }
          );
        }
      }

      form.setFieldValue("status", "RELEASED");
      saveModeRef.current = "APPROVE";
      // Give store a tick to update before submitting
      setTimeout(() => {
        form.handleSubmit();
      }, 100);
    } catch(err) {
      toast.error("Lỗi khi duyệt lệnh: " + String(err));
    } finally {
      setIsApproving(false);
    }
  };

  const handleSyncAllTechSpecs = React.useCallback(() => {
    if (!fullProduct) {
      toast.error("Không tìm thấy thông tin Sản phẩm. Vui lòng chọn lại.");
      return;
    }

    let bomCount = 0;
    let routingCount = 0;
    let toolingCount = 0;

    // 1. Đồng bộ Vật tư (BOM) - Tương đương BOMBuilder handleAutoFill
    if (fullProduct.productMaterials) {
      const newBOMItems = fullProduct.productMaterials
        .filter((m: any) => !m.isDeleted)
        .map((m: any) => ({
          materialId: m.materialId || "",
          qtyRequired: m.quantity || 0,
          wastagePercent: m.wastePercent || 0,
          unit: m.material?.unit || "",
          notes: m.note || "",
        }));
      
      if (newBOMItems.length > 0) {
        bomCount = newBOMItems.length;
        const currentComponents = form.getFieldValue("components") || [];
        const newComponents = [...currentComponents];
        newComponents[0] = {
          ...(newComponents[0] || {}),
          isMain: true,
          componentName: "Main",
          bomItems: newBOMItems,
        };
        form.setFieldValue("components", newComponents);
      }
    }

    // 2. Đồng bộ Quy trình (Routing) - Tương đương RoutingBuilder handleAutoFill
    if (fullProduct.productProcesses) {
      const newRoutingSteps = mergeUniqueRoutingSteps(
        fullProduct.productProcesses,
        [],
      );

      if (newRoutingSteps.length > 0) {
        routingCount = newRoutingSteps.length;
        const currentComponents = form.getFieldValue("components") || [];
        const newComponents = [...currentComponents];
        newComponents[0] = {
          ...(newComponents[0] || {}),
          isMain: true,
          componentName: "Main",
          routingSteps: newRoutingSteps,
        };
        form.setFieldValue("components", newComponents);
      }
    }

    // 3. Đồng bộ Kế hoạch Kẽm CTP & Khuôn bế riêng biệt
    const newToolings: any[] = [];
    let plateCount = 0;
    let mouldCount = 0;

    // 3.1 Bản kẽm CTP: chỉ khi sản phẩm có khai báo màu in (không mặc định 4 màu, không lấy khổ giấy làm khổ kẽm)
    const plateSpecsSize = fullProduct.specs?.plateSize || "";
    const colorsList: string[] = Array.isArray(fullProduct.specs?.colors) ? fullProduct.specs.colors : [];

    colorsList.forEach((color: string, idx: number) => {
      newToolings.push({
        id: `ctp-plate-${idx + 1}`,
        toolingId: `CTP-${idx + 1}`,
        toolingType: "PLATE",
        status: "NEW",
        plateColor: color,
        plateSize: plateSpecsSize || null,
        plateSource: "INTERNAL",
        quantity: 1,
        notes: "",
      });
      plateCount++;
    });

    // 3.2 Khuôn bế: Lấy từ fullProduct.dieCuts (Tài sản tái sử dụng từ kho)
    if (fullProduct.dieCuts && Array.isArray(fullProduct.dieCuts)) {
      fullProduct.dieCuts.forEach((dc: any) => {
        if (!dc.isDeleted && dc.status !== 'DELETED') {
          newToolings.push({
            id: dc.id,
            toolingId: dc.id,
            toolingType: "DIE_CUT",
            status: "REUSE",
            location: dc.location || "",
            notes: dc.name || dc.code || "",
          });
          mouldCount++;
        }
      });
    }

    if (newToolings.length > 0) {
      toolingCount = newToolings.length;
      form.setFieldValue("toolings", newToolings);
      
      const newDieCuts = newToolings
        .filter((t: any) => t.toolingType === "DIE_CUT")
        .map((t: any) => t.toolingId);
      const newPlates = newToolings
        .filter((t: any) => t.toolingType === "PLATE")
        .map((t: any) => t.toolingId);
      form.setFieldValue("dieCutIds", newDieCuts);
      form.setFieldValue("plateIds", newPlates);
    }

    toast.success(`Đã đồng bộ ${bomCount} Vật tư, ${routingCount} Công đoạn, ${plateCount} Bản kẽm CTP và ${mouldCount} Khuôn bế!`);

  }, [fullProduct, form]);

  return (
    <div className="space-y-4 w-full max-w-none pb-8">
      <form onSubmit={handleFormSubmit} className="space-y-4 w-full max-w-none">
        {/* Sticky Executive Action Header - Ẩn khi bọc bởi StandardDetailLayout (Rule 3) */}
        {!hideStickyHeader && (
          <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200 py-2 px-3 sm:px-4 -mx-2 sm:-mx-4 mb-3 flex flex-wrap items-center justify-between gap-2 shadow-xs">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="font-mono font-bold text-xs sm:text-sm text-indigo-700 bg-indigo-50 px-2 sm:px-2.5 py-0.5 rounded border border-indigo-200 shrink-0">
                {orderId || defaultValues?.poReference || "LỆNH SX"}
              </span>
              <form.Subscribe selector={(state) => [state.values.status, state.values.priority, state.values.productName]}>
                {([statusVal, priorityVal, prodName]) => (
                  <>
                    <Badge variant="outline" className={cn(
                      "text-[11px] font-semibold",
                      statusVal === "COMPLETED" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      statusVal === "IN_PROGRESS" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      "bg-slate-100 text-slate-700 border-slate-200"
                    )}>
                      {statusVal || "DRAFT"}
                    </Badge>
                    {priorityVal === "URGENT" && (
                      <Badge variant="destructive" className="text-[10px] animate-pulse">HỎA TỐC</Badge>
                    )}
                    <span className="text-slate-300 hidden sm:inline">•</span>
                    <span className="text-xs font-medium text-slate-600 truncate hidden md:inline max-w-[260px]" title={prodName}>
                      {prodName || "Chưa chọn sản phẩm"}
                    </span>
                  </>
                )}
              </form.Subscribe>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {isEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-amber-600 border-amber-200 hover:bg-amber-50 h-8 text-xs font-medium"
                  onClick={() => toast.success("Đã đẩy trạng thái Lệnh SX về Chờ Bình Kẽm (Prepress)")}
                  title="Yêu cầu Kỹ thuật bình lại file / kẽm"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Yêu cầu bình lại</span>
                </Button>
              )}
              {handleSyncAllTechSpecs && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 h-8 text-xs font-medium"
                  onClick={handleSyncAllTechSpecs}
                  title="Đồng bộ lại toàn bộ thông số kỹ thuật, BOM, Công đoạn & Khuôn từ Master Product"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Đồng bộ</span> TCKT
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCancel}
                className="h-8 text-xs min-w-[60px]"
              >
                Hủy
              </Button>
              <SaveActionButtons
                status={isPublishedOrder ? currentStatus : (currentStatus === "PENDING_APPROVAL" ? "PENDING_APPROVAL" : undefined)}
                busy={Boolean(isSubmitting || isDraftSaving)}
                onSaveDraft={handleSaveDraft}
                submitLabel={submitLabel}
              />
            </div>
          </div>
        )}

        <div className="w-full">
          <Tabs defaultValue="products" className="w-full">
            {/* 5 Ergonomic Tabs corresponding 1:1 with ProductionOrderView */}
            <div className={cn(
              "border-b border-slate-200 bg-white sticky z-20 -mx-2 sm:-mx-4 px-2 sm:px-4",
              hideStickyHeader ? "top-0" : "top-[49px]"
            )}>
              <TabsList>
                <TabsTrigger
                  value="products"
                >
                  <Layers className="h-4 w-4 mr-1.5 text-indigo-600 shrink-0" />
                  1. Danh mục sản phẩm
                </TabsTrigger>
                <TabsTrigger
                  value="materials"
                >
                  <Package className="h-4 w-4 mr-1.5 text-indigo-600 shrink-0" />
                  2. Kế hoạch vật tư
                </TabsTrigger>
                <TabsTrigger
                  value="toolings"
                >
                  <Wrench className="h-4 w-4 mr-1.5 text-emerald-600 shrink-0" />
                  3. Kế hoạch khuôn kẽm
                </TabsTrigger>
                <TabsTrigger
                  value="operations"
                >
                  <Factory className="h-4 w-4 mr-1.5 text-indigo-600 shrink-0" />
                  4. Kế hoạch sản xuất
                </TabsTrigger>
                <TabsTrigger
                  value="finishing"
                >
                  <ShieldCheck className="h-4 w-4 mr-1.5 text-indigo-600 shrink-0" />
                  5. KCS &amp; Đóng gói
                </TabsTrigger>
              </TabsList>
            </div>

            {/* TAB 1: 1. Danh mục sản phẩm & Thông tin chung */}
            <TabsContent value="products" className="space-y-6 mt-4">
              <POHeader
                form={form}
                orderData={orderData}
                selectedCustomerId={selectedCustomerId}
                selectedSalesOrderId={selectedSalesOrderId}
                onSyncTechSpecs={handleSyncAllTechSpecs}
                defaultValues={defaultValues}
                isEdit={isEdit}
              />

              {/* Danh sách Sản phẩm trong lệnh / Ghép bài */}
              <Card className="border-none shadow-sm ring-1 ring-slate-200">
                <CardHeader className="bg-slate-50/50 py-2.5 px-4 border-b">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                    <Package className="h-4 w-4 text-slate-500" /> Danh mục Sản phẩm / Ghép bài trong Lệnh
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <OrderSourceBuilder form={form} />
                </CardContent>
              </Card>

              {/* Cấu trúc hợp phần: cây Sản phẩm → Hợp phần (vật tư, khuôn/kẽm, công đoạn khai báo theo hợp phần ở các tab sau) */}
              <Card className="border-none shadow-sm ring-1 ring-slate-200">
                <CardHeader className="bg-slate-50/50 py-2.5 px-4 border-b">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                    <Layers className="h-4 w-4 text-slate-500" /> Cấu trúc hợp phần
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ComponentTreeEditor form={form} />
                </CardContent>
              </Card>

              {/* Thông số Kỹ thuật Chế bản & Vật tư Giấy */}
              <Card className="border-none shadow-sm ring-1 ring-slate-200">
                <CardHeader className="bg-slate-50/50 py-2.5 px-4 border-b">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                    <Printer className="h-4 w-4 text-indigo-500" /> Thông số Kỹ thuật Chế bản & Vật tư Giấy
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-6">
                  {/* Khối 1: Quy cách in & Bình bản */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">1. Quy cách in & Bình bản</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <form.Field name="printingPaperSize">
                        {(field) => <SizeField field={field} label="Khổ giấy in" />}
                      </form.Field>
                      <form.Field name="prepressPcsPerSheet">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="Số bát / tờ in">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => {
                                  const pcs = Number(e.target.value);
                                  field.handleChange(pcs);
                                  const targetQty = form.getFieldValue("totalTargetQty") || form.getFieldValue("qtyPlan") || 0;
                                  const waste = form.getFieldValue("prepressWasteAllowance") || 0;
                                  if (pcs > 0) {
                                    const sheets = Math.ceil(targetQty / pcs) + waste;
                                    form.setFieldValue("totalPlanSheets", sheets);
                                    const cutRatio = form.getFieldValue("cutRatio") || 1;
                                    form.setFieldValue("totalMaterialRequired", Math.ceil(sheets / cutRatio));
                                  }
                                }}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                      <form.Field name="prepressWastePercentage">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="% Bù hao in">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => {
                                  const pct = Number(e.target.value);
                                  field.handleChange(pct);
                                  const targetQty = form.getFieldValue("totalTargetQty") || form.getFieldValue("qtyPlan") || 0;
                                  const pcs = form.getFieldValue("prepressPcsPerSheet") || 1;
                                  if (pcs > 0 && targetQty > 0) {
                                    const baseSheets = Math.ceil(targetQty / pcs);
                                    const wasteSheets = Math.ceil(baseSheets * (pct / 100));
                                    form.setFieldValue("prepressWasteAllowance", wasteSheets);
                                    const totalSheets = baseSheets + wasteSheets;
                                    form.setFieldValue("totalPlanSheets", totalSheets);
                                    const cutRatio = form.getFieldValue("cutRatio") || 1;
                                    form.setFieldValue("totalMaterialRequired", Math.ceil(totalSheets / cutRatio));
                                  }
                                }}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                      <form.Field name="prepressWasteAllowance">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="SL bù hao in">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => {
                                  const waste = Number(e.target.value);
                                  field.handleChange(waste);
                                  const targetQty = form.getFieldValue("totalTargetQty") || form.getFieldValue("qtyPlan") || 0;
                                  const pcs = form.getFieldValue("prepressPcsPerSheet") || 1;
                                  if (pcs > 0 && targetQty > 0) {
                                    const baseSheets = Math.ceil(targetQty / pcs);
                                    const pct = (waste / baseSheets) * 100;
                                    form.setFieldValue("prepressWastePercentage", Number(pct.toFixed(2)));
                                    const totalSheets = baseSheets + waste;
                                    form.setFieldValue("totalPlanSheets", totalSheets);
                                    const cutRatio = form.getFieldValue("cutRatio") || 1;
                                    form.setFieldValue("totalMaterialRequired", Math.ceil(totalSheets / cutRatio));
                                  }
                                }}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <form.Field name="totalTargetQty">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="Tổng SL yêu cầu">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => {
                                  const targetQty = Number(e.target.value);
                                  field.handleChange(targetQty);
                                  const pcs = form.getFieldValue("prepressPcsPerSheet") || 1;
                                  const waste = form.getFieldValue("prepressWasteAllowance") || 0;
                                  if (pcs > 0) {
                                    const totalSheets = Math.ceil(targetQty / pcs) + waste;
                                    form.setFieldValue("totalPlanSheets", totalSheets);
                                    const cutRatio = form.getFieldValue("cutRatio") || 1;
                                    form.setFieldValue("totalMaterialRequired", Math.ceil(totalSheets / cutRatio));
                                  }
                                }}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                      <form.Field name="totalPlanSheets">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="Tổng tờ in kế hoạch">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => {
                                  const sheets = Number(e.target.value);
                                  field.handleChange(sheets);
                                  const cutRatio = form.getFieldValue("cutRatio") || 1;
                                  form.setFieldValue("totalMaterialRequired", Math.ceil(sheets / cutRatio));
                                }}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                    </div>
                  </div>

                  <hr className="border-slate-100" />

                  {/* Khối 2: Vật tư giấy & Khổ xả */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">2. Vật tư giấy & Khổ xả</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <form.Field name="purchasedPaperSize">
                        {(field) => <SizeField field={field} label="Khổ giấy mua / Khổ xả" />}
                      </form.Field>
                      <form.Field name="cutRatio">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="Tỉ lệ xả">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => {
                                  const ratio = Number(e.target.value);
                                  field.handleChange(ratio);
                                  const totalSheets = form.getFieldValue("totalPlanSheets") || 0;
                                  if (ratio > 0) {
                                    form.setFieldValue("totalMaterialRequired", Math.ceil(totalSheets / ratio));
                                  }
                                }}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                      <form.Field name="totalMaterialRequired">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="Tổng giấy cần xuất">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => field.handleChange(Number(e.target.value))}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                      <form.Field name="specification">
                        {(field) => <TextField field={field} label="Quy cách giấy & Gia công" placeholder="Vd: Ivory 300gsm, Cán màng mờ, Bồi sóng E" />}
                      </form.Field>
                      <form.Field name="standardQuantity">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="SL định mức">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => field.handleChange(Number(e.target.value))}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                      <form.Field name="allowance">
                        {(field) => (
                          <TanstackFieldWrapper field={field} label="Bù hao giấy">
                            <TanstackFormControl>
                              <NumericInput
                                value={field.state.value || ""}
                                onChange={(e) => field.handleChange(Number(e.target.value))}
                              />
                            </TanstackFormControl>
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bảng Quản lý Bài bình Layout */}
              <PrintLayoutBuilder form={form} />

              {/* Ghi chú nội bộ */}
              <Card className="border-none shadow-sm ring-1 ring-slate-200">
                <CardHeader className="bg-slate-50/50 py-2.5 px-4 border-b">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-500" /> Ghi chú chung của lệnh
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <form.Field name="notes">
                    {(field: any) => (
                      <TanstackFieldWrapper field={field} label="Ghi chú nội bộ / Chỉ đạo kỹ thuật">
                        <TanstackFormControl>
                          <Textarea
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="Nhập ghi chú yêu cầu kỹ thuật, lưu ý chuyền in, điều kiện đóng gói..."
                            className="min-h-[80px]"
                          />
                        </TanstackFormControl>
                      </TanstackFieldWrapper>
                    )}
                  </form.Field>
                </CardContent>
              </Card>

            </TabsContent>

            {/* TAB 2: Kế hoạch Vật tư & Kẽm CTP */}
            <TabsContent value="materials" className="space-y-6 mt-4">
              <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                <span className="text-xs text-slate-600 font-medium">Định mức vật tư theo từng hợp phần (cấp phát và cấp bù thực hiện ở view lệnh sau khi lưu)</span>
                <div className="flex items-center space-x-4">
                  <form.Field name="isComposite">
                    {(field: any) => (
                      <div className="flex items-center space-x-2">
                        <Switch checked={field.state.value} onCheckedChange={(val) => field.handleChange(val)} />
                        <Label className="text-xs font-semibold text-slate-700">Sản phẩm bộ</Label>
                      </div>
                    )}
                  </form.Field>
                </div>
              </div>

              <form.Field name="components" mode="array">
                {(field: any) => {
                  const components = field.state.value || [];
                  if (components.length === 0) {
                    setTimeout(() => {
                      field.pushValue({ componentName: "Main", isMain: true, routingSteps: [], bomItems: [] });
                    }, 0);
                    return null;
                  }
                  const isComposite = Boolean(form.getFieldValue("isComposite") || components.length > 1);
                  if (!isComposite) {
                    return (
                      <BOMBuilder form={form} componentIndex={0} productionOrderId={orderId || (defaultValues as any)?.id} />
                    );
                  }
                  return (
                    <div className="space-y-6">
                      {components.map((c: any, idx: number) => (
                        <div key={`bom-comp-${idx}`} className="bg-white border border-indigo-100 rounded-xl p-4 shadow-sm">
                          <div className="mb-4 flex items-center justify-between border-b pb-2">
                            <span className="text-xs font-bold uppercase text-indigo-700 flex items-center gap-1.5">
                              <Package className="w-3.5 h-3.5" /> {c.componentName || `Thành phần ${idx + 1}`}
                            </span>
                          </div>
                          <BOMBuilder form={form} componentIndex={idx} productionOrderId={orderId || (defaultValues as any)?.id} />
                        </div>
                      ))}
                    </div>
                  );
                }}
              </form.Field>

              {/* Kế hoạch Nhu cầu Vật tư */}
              <Card className="border-none shadow-sm ring-1 ring-slate-200">
                <CardHeader className="bg-slate-50/50 py-2.5 px-4 border-b">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                    <Package className="h-4 w-4 text-slate-500" /> Kế hoạch vật tư
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <MaterialPlanList
                    form={form}
                    onRegenerateAutoPlans={runAutoPlanAllSubTables}
                  />
                </CardContent>
              </Card>

            </TabsContent>

            {/* TAB 3: 3. Kế hoạch khuôn kẽm */}
            <TabsContent value="toolings" className="space-y-6 mt-4">
              {/* Khuôn & kẽm của lệnh: cùng thứ tự với view (khuôn trước, kẽm sau) */}
              <Card className="border-none shadow-sm ring-1 ring-slate-200" data-testid="po-form-toolings">
                <CardHeader className="bg-slate-50/50 py-2.5 px-4 border-b">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-emerald-600" /> Khuôn &amp; kẽm của lệnh
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-6">
                  <ToolingBuilder
                    form={form}
                    productionOrderId={orderId || (defaultValues as any)?.id}
                    productDimensions={productInfo ? {
                      length: productInfo.length,
                      width: productInfo.width,
                      height: productInfo.height,
                    } : undefined}
                  />
                  <PlatePlanBuilder
                    form={form}
                    productionOrderId={orderId || (defaultValues as any)?.id}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB 4: 4. Kế hoạch sản xuất */}
            <TabsContent value="operations" className="space-y-6 mt-4">
              <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                <span className="text-xs text-slate-600 font-medium">Chuỗi công đoạn sản xuất, gán máy &amp; thiết lập định mức công suất</span>
                <form.Field name="isComposite">
                  {(field: any) => (
                    <div className="flex items-center space-x-2">
                      <Switch checked={field.state.value} onCheckedChange={(val) => field.handleChange(val)} />
                      <Label className="text-xs font-semibold text-slate-700">Sản phẩm bộ</Label>
                    </div>
                  )}
                </form.Field>
              </div>

              <form.Field name="components" mode="array">
                {(field: any) => {
                  const components = field.state.value || [];
                  if (components.length === 0) {
                    setTimeout(() => {
                      field.pushValue({ componentName: "Main", isMain: true, routingSteps: [], bomItems: [] });
                    }, 0);
                    return null;
                  }
                  const isComposite = Boolean(form.getFieldValue("isComposite") || components.length > 1);
                  if (!isComposite) {
                    return (
                      <RoutingBuilder form={form} componentIndex={0} productionOrderId={orderId || (defaultValues as any)?.id} />
                    );
                  }
                  return (
                    <div className="space-y-6">
                      {components.map((c: any, idx: number) => (
                        <div key={`routing-comp-${idx}`} className="bg-white border border-indigo-100 rounded-xl p-4 shadow-sm">
                          <div className="mb-4 flex items-center justify-between border-b pb-2">
                            <span className="text-xs font-bold uppercase text-indigo-700 flex items-center gap-1.5">
                              <Cpu className="w-3.5 h-3.5" /> {c.componentName || `Thành phần ${idx + 1}`}
                            </span>
                          </div>
                          <RoutingBuilder form={form} componentIndex={idx} productionOrderId={orderId || (defaultValues as any)?.id} />
                        </div>
                      ))}
                    </div>
                  );
                }}
              </form.Field>
            </TabsContent>

            {/* TAB 5: Nhật ký đóng gói — bảng chỉ đọc các phiếu KCS & Đóng gói của lệnh (ghi tại trang KCS & Đóng gói) */}
            <TabsContent value="finishing" className="space-y-6 mt-4">
              <POPackagingLogTab productionOrderId={productionOrderDbId} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="sticky bottom-0 z-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 p-4 sm:p-6 bg-white/95 backdrop-blur-md border-t border-slate-200 mt-6 flex justify-between gap-3 shadow-[0_-4px_6px_-1px_rgb(0,0,0,0.05)]">
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="min-w-[100px]"
            >
              Huỷ
            </Button>
          </div>
          <div className="flex gap-3">
            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.isSubmitting,
                state.isDirty,
                state.errors,
                state.values,
              ]}
            >
              {([canSubmit, isSubmitting, isDirty, errors, values]) => (
                <>
                  <div
                    id="debug-po-form"
                    className="hidden"
                    data-cansubmit={canSubmit}
                    data-isdirty={isDirty}
                  >
                    {JSON.stringify(errors)}
                  </div>
                  {values.status !== "RELEASED" && canApproveRelease && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleApprove}
                      disabled={Boolean(!canSubmit || isSubmitting || isApproving)}
                      className="min-w-[170px] border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 font-semibold gap-1.5 shadow-2xs"
                    >
                      {isApproving ? (
                        <>
                          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Đang kiểm tra...
                        </>
                      ) : (
                        <>
                          <Rocket className="mr-1.5 h-4 w-4 text-emerald-600" /> Lưu & Duyệt Ban Hành
                        </>
                      )}
                    </Button>
                  )}
                  <SaveActionButtons
                    status={isPublishedOrder ? currentStatus : (currentStatus === "PENDING_APPROVAL" ? "PENDING_APPROVAL" : undefined)}
                    busy={Boolean(isSubmitting || isApproving || isDraftSaving)}
                    submitDisabled={!canSubmit}
                    onSaveDraft={handleSaveDraft}
                    submitLabel={submitLabel}
                  />
                </>
              )}
            </form.Subscribe>
          </div>
        </div>

      </form>

    </div>
  );
}
